/**
 * Reconciliation API Client
 * Feature: 029-reconciliation-services (T060)
 * Constitution Version: 1.11.0
 *
 * API client for reconciliation endpoints
 * T024: Updated to use centralized apiClient for auth interceptor
 */

// T024: Use centralized apiClient for auth interceptor and error handling
import { apiClient } from './apiClient';
import type {
  ReconciliationStatusResponse,
  ReconciliationRunsResponse,
  ReconciliationRunDetailResponse,
  PaginatedLogs,
} from '../types/reconciliation.types';

// API base path for reconciliation endpoints
const API_PATH = '/api/reconciliation';

export const reconciliationApi = {
  /**
   * GET /api/reconciliation/status
   * Fetch dashboard status cards for all services
   */
  async getStatus(): Promise<ReconciliationStatusResponse> {
    const response = await apiClient.get<ReconciliationStatusResponse>(`${API_PATH}/status`);
    return response.data;
  },

  /**
   * GET /api/reconciliation/runs
   * Fetch recent runs with optional filters
   */
  async getRuns(params?: {
    service_name?: string;
    status?: 'completed' | 'failed' | 'in_progress';
    limit?: number;
  }): Promise<ReconciliationRunsResponse> {
    const response = await apiClient.get<ReconciliationRunsResponse>(`${API_PATH}/runs`, { params });
    return response.data;
  },

  /**
   * GET /api/reconciliation/runs/:runId
   * Fetch single run details with logs
   */
  async getRunById(runId: string): Promise<ReconciliationRunDetailResponse> {
    const response = await apiClient.get<ReconciliationRunDetailResponse>(`${API_PATH}/runs/${runId}`);
    return response.data;
  },

  /**
   * GET /api/reconciliation/logs
   * Fetch paginated logs with filters
   */
  async getLogs(params?: {
    service_name?: string;
    severity?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
    entity_type?: 'workspace' | 'service_principal' | 'agent';
    entity_id?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedLogs> {
    const response = await apiClient.get<PaginatedLogs>(`${API_PATH}/logs`, { params });
    return response.data;
  },

  /**
   * POST /api/reconciliation/runs/:serviceName
   * Manually trigger a reconciliation run
   */
  async triggerRun(serviceName: 'AgentDeployment' | 'WorkspaceMetadata'): Promise<{
    message: string;
    service_name: string;
    correlation_id: string;
  }> {
    const response = await apiClient.post(`${API_PATH}/runs/${serviceName}`);
    return response.data;
  },

  /**
   * GET /api/reconciliation/scheduler/status
   * Fetch scheduler state
   */
  async getSchedulerStatus(): Promise<{
    isShuttingDown: boolean;
    tasks: Array<{
      name: string;
      schedule: string;
      isRunning: boolean;
      lastRun: string | null;
      nextRun: string | null;
    }>;
  }> {
    const response = await apiClient.get(`${API_PATH}/scheduler/status`);
    return response.data;
  },

  /**
   * POST /api/reconciliation/mass-deletion/:runId/approve
   * Approve mass deletion
   */
  async approveMassDeletion(runId: string): Promise<{
    message: string;
    run_id: string;
    approved_by: string;
    approved_at: string;
  }> {
    const response = await apiClient.post(`${API_PATH}/mass-deletion/${runId}/approve`);
    return response.data;
  },
};
